import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { ParkingMainRateDto, ParkingMainRateMaster, ParkingPenaltyRateDetail, ParkingSubRateDetail } from '../../../../core/models/system-settings/parking-master-data.model';
import { MapperParkingMasterDataService } from '../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiParkingMasterDataService } from '../../../../core/services/api/system-settings/api-parking-master-data.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalParkingMainRateComponent } from '../../modals/system-settings/modal-parking-main-rate/modal-parking-main-rate.component';

@Component({
  selector: 'app-parking-main-rate',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalParkingMainRateComponent],
  templateUrl: './parking-main-rate.component.html',
  styleUrl: './parking-main-rate.component.css',
})
export class ParkingMainRateComponent implements OnInit {
  private readonly api = inject(ApiParkingMasterDataService);
  private readonly mapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  items = signal<ParkingMainRateMaster[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  selectedId = signal(0);
  searchText = signal('');
  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions: number[] = [10, 20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(1);
  lastFetchedTime: Date | null = null;

  get canCreate() { return this.permissionService.canCreate(this.router.url); }
  get canUpdate() { return this.permissionService.canUpdate(this.router.url); }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(this.totalPages(), startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  ngOnInit(): void { this.loadTable(); }
  onSearchChange(): void { this.currentPage.set(1); this.loadTable(); }
  onRefresh(): void { this.currentPage.set(1); this.loadTable(); }

  openCreate(): void {
    if (!this.canCreate) { this.toast.error('ไม่มีสิทธิ์สร้าง'); return; }
    this.selectedId.set(0);
    this.isModalOpen.set(true);
  }

  openEdit(item: ParkingMainRateMaster): void {
    if (!this.canUpdate) { this.toast.error('ไม่มีสิทธิ์แก้ไข'); return; }
    this.selectedId.set(item.id);
    this.isModalOpen.set(true);
  }

  closeModal(): void { this.isModalOpen.set(false); this.selectedId.set(0); }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) { this.currentPage.set(page); this.loadTable(); }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadTable();
  }

  trackById(_: number, item: ParkingMainRateMaster): number { return item.id; }

  subSummary(detail: ParkingSubRateDetail | null | undefined, fallbackId: string): string {
    return this.mapper.formatSubRateSummary(detail, fallbackId);
  }

  penaltySummary(detail: ParkingPenaltyRateDetail | null | undefined, fallbackId: string): string {
    return this.mapper.formatPenaltySummary(detail, fallbackId);
  }

  loadTable(): void {
    this.isLoading.set(true);
    this.api.getParkingMainRateForTable({
      page: this.currentPage(),
      pageSize: this.pageSize(),
      searchText: this.searchText().trim() || undefined,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapTable<ParkingMainRateDto, ParkingMainRateMaster>(
          res,
          (item) => this.mapper.mapMainRate(item)
        );
        this.items.set(mapped.data);
        this.totalItems.set(mapped.total);
        this.totalPages.set(Math.max(1, Math.ceil(mapped.total / this.pageSize())));
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: () => { this.items.set([]); this.isLoading.set(false); this.toast.error('โหลดข้อมูลล้มเหลว'); },
    });
  }
}
